import { resolveRow } from '../shape-map/resolver';
import { buildIdentityAliases } from './predecessor-identity';

export function resolveEligibleRows(composedMap, rows) {
  return rows
    .map(row => ({ row, resolved: resolveRow(composedMap, row) }))
    .filter(
      ({ resolved }) =>
        resolved.renderMode === 'diff' &&
        resolved.identity.resolved &&
        entityHasRecordActions(composedMap, resolved.identity.entity)
    );
}

// A legacy alias can resolve to an entity no entry records (SCHEMA_LIBRARY_PUBLISHED's
// schemaLibraryVersion); its lookup would bound actions to an empty list and never match.
function entityHasRecordActions(composedMap, entity) {
  return buildIdentityAliases(composedMap, entity).recordActions.length > 0;
}

function groupKey(resolved) {
  return `${resolved.identity.entity}:${resolved.identity.recordKey}`;
}

function groupByIdentity(eligibleRows) {
  const groups = new Map();
  for (const eligible of eligibleRows) {
    const key = groupKey(eligible.resolved);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(eligible);
  }
  return groups;
}

// An unfiltered page is a contiguous slice of an identity's history only when every row of that
// identity reached the page through an id. An unattributed row did not: a name-attributed row is
// scoped by the parent's CURRENT name, and a legacy join row (identityNotNavigable) carries no
// parent id at all, so it too reaches the parent's page only through a name. A row written while the
// parent bore a former name is then absent with no filter applied — and a rename-and-back puts that
// gap between two visible rows, where the neighbour below is no longer the predecessor.
function pageHoldsContiguousHistory(group) {
  return group.every(({ resolved }) => !resolved.unattributed);
}

export function selectAliasTargets(eligibleRows, { hasActiveFilters }) {
  if (hasActiveFilters) return eligibleRows;

  const aliasedRowIds = new Set();
  for (const group of groupByIdentity(eligibleRows).values()) {
    const aliased = pageHoldsContiguousHistory(group) ? [group[group.length - 1]] : group;
    for (const eligible of aliased) aliasedRowIds.add(eligible.row.id);
  }

  return eligibleRows.filter(({ row }) => aliasedRowIds.has(row.id));
}

export function buildPredecessorMap(eligibleRows, { hasActiveFilters, fetchedByRowId }) {
  const predecessorMap = new Map();

  if (hasActiveFilters) {
    for (const eligible of eligibleRows) {
      predecessorMap.set(eligible.row.id, fetchedByRowId.get(eligible.row.id) ?? null);
    }
    return predecessorMap;
  }

  for (const group of groupByIdentity(eligibleRows).values()) {
    if (!pageHoldsContiguousHistory(group)) {
      for (const eligible of group) {
        predecessorMap.set(eligible.row.id, fetchedByRowId.get(eligible.row.id) ?? null);
      }
      continue;
    }

    // Only the on-page NEXT row of the same identity is used directly here, never whatever row sits
    // next in the fetched stream — the neighbour is provably this identity's own preceding row because
    // the gate above established that the page holds this identity's history contiguously.
    group.forEach((current, index) => {
      const next = group[index + 1];
      predecessorMap.set(current.row.id, next ? next.row : fetchedByRowId.get(current.row.id) ?? null);
    });
  }

  return predecessorMap;
}
